import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isServiceableSubscriptionStatus } from '@bruno/shared';
import { Badge, Button, Card, Input } from '@bruno/ui';
import {
  getBalance,
  getUsage,
  listMySubscriptions,
  listBalanceTransactions,
  openBillingPortal,
  startTopUp,
} from '../api/billing';

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function BillingPage() {
  const balance = useQuery({ queryKey: ['balance'], queryFn: getBalance });
  const subs = useQuery({ queryKey: ['my-subs'], queryFn: listMySubscriptions });
  const txs = useQuery({ queryKey: ['my-tx'], queryFn: listBalanceTransactions });
  const usage = useQuery({ queryKey: ['my-usage'], queryFn: getUsage });
  const [topupAmount, setTopupAmount] = useState('20');
  const [error, setError] = useState<string | null>(null);

  const topup = useMutation({
    mutationFn: () =>
      startTopUp({ amountCents: Math.round(Number(topupAmount) * 100) }),
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
    onError: (err: Error) => setError(err.message),
  });

  const portal = useMutation({
    mutationFn: openBillingPortal,
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
    onError: (err: Error) => setError(err.message),
  });

  const inactiveSubs =
    subs.data?.subscriptions.filter((s) => !isServiceableSubscriptionStatus(s.status)) ?? [];

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold">Billing</h1>

      {inactiveSubs.length > 0 ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="font-semibold text-red-700">Action required: payment failed</div>
              <p className="mt-1 text-sm text-red-700/90">
                {inactiveSubs.length === 1 ? 'One website is' : `${inactiveSubs.length} websites are`}{' '}
                paused because a payment didn&apos;t go through. Bruno is hidden on{' '}
                {inactiveSubs.length === 1 ? 'it' : 'them'} until the invoice is paid. Update your
                payment method to restore service automatically.
              </p>
              <ul className="mt-2 list-inside list-disc text-xs text-red-700/90">
                {inactiveSubs.map((s) => (
                  <li key={s.id}>
                    {s.websiteName} ({s.websiteDomain}) — {s.status}
                  </li>
                ))}
              </ul>
            </div>
            <Button onClick={() => portal.mutate()} loading={portal.isPending}>
              Update payment method
            </Button>
          </div>
        </div>
      ) : null}

      <Card>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-sm text-slate-500">Wallet balance</div>
            <div className="text-3xl font-semibold">
              {balance.data ? dollars(balance.data.balanceCents) : '—'}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Used to cover AI usage beyond your plan's included tokens.
            </p>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Input
                label="Top-up amount (USD)"
                type="number"
                min="5"
                max="1000"
                step="5"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
              />
            </div>
            <Button onClick={() => topup.mutate()} loading={topup.isPending}>
              Add funds
            </Button>
          </div>
        </div>
        {error ? <div className="mt-2 text-sm text-red-600">{error}</div> : null}
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">Subscriptions</h2>
          <Button variant="secondary" size="sm" onClick={() => portal.mutate()} loading={portal.isPending}>
            Manage in Stripe portal
          </Button>
        </div>
        {!subs.data || subs.data.subscriptions.length === 0 ? (
          <div className="text-sm text-slate-500 italic">
            No subscriptions yet. Open a website and pick a plan to subscribe.
          </div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {subs.data.subscriptions.map((s) => (
              <li key={s.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium">{s.websiteName}</div>
                  <div className="text-xs text-slate-500">{s.websiteDomain}</div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge tone={s.status === 'active' || s.status === 'trialing' ? 'green' : s.status === 'past_due' ? 'amber' : 'red'}>
                    {s.status}
                  </Badge>
                  {s.currentPeriodEnd ? (
                    <span className="text-xs text-slate-500">
                      Renews {new Date(s.currentPeriodEnd).toLocaleDateString()}
                    </span>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <h2 className="font-semibold mb-3">Usage by website</h2>
        {!usage.data || usage.data.websites.length === 0 ? (
          <div className="text-sm text-slate-500 italic">No websites yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-2">Website</th>
                  <th>Plan</th>
                  <th>Conversations</th>
                  <th>Tokens used</th>
                  <th>Cost (plan rate)</th>
                </tr>
              </thead>
              <tbody>
                {usage.data.websites.map((w) => {
                  const included = w.plan?.includedTokens ?? 0;
                  const used = w.subscription?.usageTokens ?? 0;
                  const pct = included > 0 ? Math.min(100, (used / included) * 100) : 0;
                  return (
                    <tr key={w.websiteId} className="border-b border-slate-100 last:border-0 align-top">
                      <td className="py-2">
                        <div className="font-medium">{w.websiteName}</div>
                        <div className="text-xs text-slate-500">{w.websiteDomain}</div>
                      </td>
                      <td>
                        {w.plan ? (
                          <div>
                            <div>{w.plan.name}</div>
                            {included > 0 ? (
                              <div className="mt-1 w-32 h-1.5 bg-slate-100 rounded overflow-hidden">
                                <div
                                  className={`h-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-indigo-500'}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            ) : null}
                            {included > 0 ? (
                              <div className="text-xs text-slate-500 mt-0.5">
                                {used.toLocaleString()} / {included.toLocaleString()} tokens
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">no plan</span>
                        )}
                      </td>
                      <td>{w.totals.conversations}</td>
                      <td>{w.totals.tokens.toLocaleString()}</td>
                      <td>{dollars(w.totals.costCents)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <h2 className="font-semibold mb-3">Recent transactions</h2>
        {!txs.data || txs.data.transactions.length === 0 ? (
          <div className="text-sm text-slate-500 italic">No transactions yet.</div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {txs.data.transactions.map((t) => (
              <li key={t.id} className="py-2 flex items-center justify-between text-sm">
                <span className="text-slate-700">{t.reason}</span>
                <div className="flex items-center gap-3">
                  <span className={t.deltaCents >= 0 ? 'text-green-700' : 'text-red-700'}>
                    {t.deltaCents >= 0 ? '+' : ''}
                    {dollars(t.deltaCents)}
                  </span>
                  <span className="text-slate-400 text-xs">
                    {new Date(t.createdAt).toLocaleString()}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
